import React from "react";
import {
    Col,
    DatePicker,
    Divider,
    Drawer,
    Form,
    Image,
    Input,
    InputNumber, notification,
    Radio,
    Row,
    Select,
    Tabs, Tooltip, Typography,
    Upload
} from "antd";
import { Button } from "src/components/actions/button";
import { CloseSvg, RotateClockwiseSvg, SaveSvg } from "src/components/Svg";
import { TranslatableInput } from "src/components/Translatable";
import { useAppData } from "src/store/app-data";
import {
    emailValidation, formatEmailValidation, formatPhoneValidation,
    getFormattedLangData,
    getFormData,
    getInitialValues,
    getNestedFormData,
    getOldImage, phoneValidation,
    whiteSpaceValidation
} from "src/utils/helpers";
import styled from "styled-components";
import {AuditOutlined, CompassOutlined, CopyOutlined, PlusOutlined, SendOutlined} from "@ant-design/icons";
import {useImage} from "../../hooks";
import moment from "moment/moment";
import api from "../../api";

export function EditDrawer({
                               title,
                               initialValues,
                               onClose,
                               onSave,
                               taskEnum = null,
                               onSendTasks,
                           }) {

    const [isLoading, setIsLoading] = React.useState(false)
    const [ telegramLink, setTelegramLink ] = React.useState(null)

    const layoutConfig = useAppData((state) => state.layoutConfig);

    const [form] = Form.useForm();


    const disabledDate = (current) => {
        return current && current >= moment();
    };

    React.useEffect(() => {
        if (initialValues) {
            let values = {
                id: initialValues.id,
                phone: initialValues.phone ?? null,
                email: initialValues.email ?? null,
                birthdayDate: initialValues.dashboardUserInfo?.birthdayDate ? moment(initialValues.dashboardUserInfo.birthdayDate, "YYYY-MM-DD") : null,
            }
            form.setFieldsValue(getInitialValues(values));
        }
    }, [initialValues]);

    function prepareData(values) {
        const id = initialValues?.id

        const data = {
            ...(id && { id, _method: "PUT", birthdayDate: moment(values.birthdayDate).format("YYYY-MM-DDTHH:mm:ssZ") }),
            phone: values.phone,
            email: values.email,
            name: values.name ?? null,
        };

        return data;
    }

    function copyToClipboard(text) {
        navigator.clipboard.writeText(text)
            .then(() => {
                console.log('Скопировано:', text);
            })
            .catch((err) => {
                console.error('Ошибка копирования:', err);
            });
    }


    async function onSubmit(values) {

        const baseData = prepareData(values)

        const formData = getFormData({
            values: baseData,
            photo: baseData.imageUpdated && baseData.photo,
        })

        const data = getFormattedLangData(values)


        await onSave(formData, values?.id);
        onClose();
        form.resetFields();
    }

    function handleClose() {
        onClose();
        setTelegramLink(null)
        form.resetFields();
    }

    function handleSendTask(task) {
        setIsLoading(true)

        try {
            const formData = new FormData();
            formData.append("task", task);
            onSendTasks(initialValues.id, formData)
        } finally {
            setIsLoading(false)
        }
    }

    async function onGetTelegramLink(id) {
        setIsLoading(true)
        try {
            const { data } = await api.get(`users/create-tg-link/${id}`)
            if (data.url) {
                setTelegramLink(data.url)
            }
        } catch (error) {
            notification.error({ message: " Ошибка получения ссылки" })
        } finally {
            setIsLoading(false)
        }
    }

    const id = initialValues?.id ?? "new";

    return (
        <DrawerEdit
            width={layoutConfig.isMobile ? "90%" : "80%"}
            title={title}
            onClose={handleClose}
            open
        >
            <FormEdit form={form} layout={"vertical"} onFinish={(values) => onSubmit(values)}>

                <StyledFormItem
                    label={"Телефон"}
                    key={id + "_phone"}
                    name={"phone"}
                    rules={[
                        {
                            required: true,
                            message: "Введите номер",
                        },
                        {
                            validator: (formatPhoneValidation)
                        },
                    ]}
                >
                    <Input placeholder={"Формат: 79*********"}/>
                </StyledFormItem>

                <StyledFormItem
                    label={"E-mail"}
                    key={id + "_email"}
                    name={"email"}
                    rules={[
                        {
                            required: true,
                        },
                        {
                            validator: (id === "new" ? emailValidation : formatEmailValidation)
                        }
                    ]}
                >
                    <Input type="email" placeholder={"name@domain.com"} />
                </StyledFormItem>

                {id !== "new" && (
                    <>
                        <StyledFormItem
                            label={"Дата рождения"}
                            key={id + "_birthdayDate"}
                            name={"birthdayDate"}
                            rules={[
                                {
                                    required: true,
                                    message: "Введите дату рождения",
                                },
                            ]}
                        >
                            <DatePicker
                                disabledDate={disabledDate}
                            />
                        </StyledFormItem>
                    </>
                )}

                {id === "new" && (
                    <>
                        <StyledFormItem
                            label={"Имя"}
                            key={id + "_name"}
                            name={"name"}
                            rules={[
                                {
                                    required: false,
                                    message: "Введите пароль",
                                },
                            ]}
                        >
                            <Input />
                        </StyledFormItem>
                    </>
                )}


                <ButtonRow>
                    <ButtonCol>
                        <Button
                            danger
                            onClick={() => {
                                if (initialValues) {
                                    form.setFieldsValue(getInitialValues(initialValues));
                                    return;
                                }
                                form.resetFields();
                            }}
                            icon={<RotateClockwiseSvg />}
                        >
                            Вернуть
                        </Button>
                    </ButtonCol>
                    <ButtonCol>
                        <Button onClick={handleClose} icon={<CloseSvg />}>
                            Закрыть
                        </Button>
                    </ButtonCol>
                    <ButtonCol>
                        <Button icon={<SaveSvg />} type="primary" htmlType="submit">
                            Сохранить
                        </Button>
                    </ButtonCol>
                </ButtonRow>
            </FormEdit>
            {initialValues?.id && (
                <>
                    <Divider>Обновление данных</Divider>
                    <FlexRow>
                        {taskEnum.map((task) => (
                            <>
                                <Button type="primary"
                                        disabled={isLoading}
                                        onClick={() => handleSendTask(task.value)}
                                >{task.label}</Button>
                            </>
                        ))}
                    </FlexRow>
                    <Divider>Телеграм</Divider>
                    <Button
                        type="primary"
                        disabled={isLoading}
                        onClick={() => onGetTelegramLink(initialValues.id)}
                        icon={
                            <SendOutlined style={{ fontSize: "16px"}}/>
                        }
                    >
                        Получить ссылку
                    </Button>
                    {telegramLink &&
                        <div style={{ marginTop: "30px"}}>
                            <Tooltip placement="bottom" title="Скопировать" mouseLeaveDelay={0}>
                                <Button size="small" type="link" onClick={() => copyToClipboard(telegramLink)}>
                                    <FlexRow>
                                        <h2 style={{ margin: 0}}>{telegramLink}</h2>
                                        <CopyOutlined style={{ fontSize: "16px" }}/>
                                    </FlexRow>
                                </Button>
                            </Tooltip>
                        </div>


                    }
                </>
            )}


        </DrawerEdit>
    );
}

const DrawerEdit = styled(Drawer)``;

const FormEdit = styled(Form)``;

const StyledFormItem = styled(Form.Item)`
  margin-bottom: 16px;
`;

const ButtonCol = styled(Col)`
  padding-bottom: 5px;
`;

const FlexRow  = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 15px;
`

const ButtonRow = styled(Row)`
  align-items: center;
  justify-content: flex-end;
  margin-top: 10px;

  .ant-col {
    margin-left: 0.4em;
  }
`;

const StyledImage = styled(Image)`
  display: none;
`

